import React, { useContext, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ButtonBase from '@mui/material/ButtonBase'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { ApplicationContext } from './MyApplication'
import { FormSection } from './FormKit'
import moduleList from './moduleList'

// Review step: every other step with its outstanding items, each one a
// shortcut back to that step. Submit unlocks once nothing is outstanding.
const Submit = () => {
  const [openConfirm, setOpenConfirm] = useState(false)
  const { requiredFields, handleSubmit, goToStep } =
    useContext(ApplicationContext)
  const ready = requiredFields.length === 0

  return (
    <Stack gap={2}>
      <FormSection
        title="Checklist"
        caption={
          ready
            ? 'Everything we need is here. Give it a final read through, then submit.'
            : `${requiredFields.length} ${
                requiredFields.length === 1 ? 'item still needs' : 'items still need'
              } completing before you can submit.`
        }
      >
        <Stack divider={<Box borderTop={1} borderColor="divider" />}>
          {moduleList
            .filter((step) => step.key !== 'review')
            .map((step) => {
              const outstanding = requiredFields.filter(
                (field) => field.step === step.key,
              )
              return (
                <ButtonBase
                  key={step.key}
                  onClick={() => goToStep(step.key)}
                  sx={{
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    gap: 1.5,
                    py: 1.25,
                    px: 1,
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'anMuted.main' },
                  }}
                >
                  {outstanding.length ? (
                    <ErrorOutlineIcon color="warning" />
                  ) : (
                    <CheckCircleIcon color="success" />
                  )}
                  <Box flex={1} minWidth={0}>
                    <Typography fontWeight={500}>{step.title}</Typography>
                    {outstanding.map((field) => (
                      <Typography
                        key={field.name}
                        variant="body2"
                        color="warning.dark"
                      >
                        {field.name}
                      </Typography>
                    ))}
                  </Box>
                  <ChevronRightIcon color="action" />
                </ButtonBase>
              )
            })}
        </Stack>
      </FormSection>

      <FormSection title="Submit your application">
        <Typography variant="body2">
          Once submitted you won't be able to change your answers, but you can
          follow your application's progress here — references, interviews and
          any offer.
        </Typography>
        {!ready && (
          <Alert severity="warning">
            Complete the items above to submit.
          </Alert>
        )}
        <Button
          variant="contained"
          color="success"
          size="large"
          disabled={!ready}
          onClick={() => setOpenConfirm(true)}
          sx={{ alignSelf: { sm: 'flex-start' } }}
        >
          Submit application
        </Button>
      </FormSection>

      <Dialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Submit your application?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            By submitting you confirm the information you've given is true and
            complete.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setOpenConfirm(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => {
              setOpenConfirm(false)
              handleSubmit()
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default Submit
